import { lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router";
import "./App.css";
import "./assets/scss/default.scss";
import AutomatedGiftsPage from "./pages/automatedGifts";
import PrivateRoute from "./components/PrivateRoute";
import Reviews from "./pages/reviews/Reviews";
import ReviewsPage from "./pages/reviews";
import Plans from "./pages/billing/Plans";
import Referral from "./pages/reviews/Referral";
import SettingsPage from "./pages/settings/SettingsPage";
import Profile from "./pages/profile";
import PaymentCard from "./pages/billing/paymentMethod/PaymentCard";
import Logout from "./pages/login/Logout";
import Invoice from "./pages/billing/invoices/Invoice";
import SignUp from "./pages/signUp";
import TeamManagement from "./pages/team/TeamManagement";
import JoinTeam from "./pages/joinTeam";
import LiveChatSidebarIntegration from "./layout/components/LiveChatSidebarIntegration";
import Gift from "./pages/gift";
import Campaign from "./pages/campaign";


const VerticalLayout = lazy(() => import("./layout/Vertical"));
const Pricing = lazy(() => import("./pages/billing"));
const SendViaLink = lazy(() => import("./pages/gift/SendViaLink"));
const BulkSend = lazy(() => import("./pages/gift/BulkSend"));
const GiftTracking = lazy(() => import("./pages/tracking"));
const Crm = lazy(() => import("./pages/crm"));
const Branding = lazy(() => import("./pages/branding"));
const SendViaEmail = lazy(() => import("./pages/gift/SendViaEmail"));
const SendViaText = lazy(() => import("./pages/gift/SendViaText"));
const Login = lazy(() => import("./pages/login"));
const ResetPassword = lazy(() => import("./pages/resetPassword"));
const Dashboard = lazy(() => import("./pages/dashboard"));
const Settings = lazy(() => import("./pages/settings"));
const TemplateList = lazy(
  () => import("./pages/settings/templates/TemplateList")
);
const TemplateView = lazy(
  () => import("./pages/settings/templates/TemplateView")
);
const AddEditTemplatePage = lazy(
  () => import("./pages/settings/templates/AddEditTemplatePage")
);
const EventManage = lazy(() => import("./pages/crm/eventManage/addEditEvent"));
const EventList = lazy(() => import("./pages/crm/eventManage/eventList"));
const NewCampaign = lazy(() => import("./pages/campaign/components/NewCampaign"));
const EditCampaign = NewCampaign;

const NotFound = () => {
  return <p>Not found</p>;
};

const LegacyCampaignEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/gift/via-campaign/${id}/edit`} replace />;
};

function App() {
  return (
    <Router>
      <LiveChatSidebarIntegration />
      <Routes>
        <Route path="/signup" element={<SignUp />} />
        <Route path="/guild-signup" element={<SignUp variant="guild" />} />
        <Route path="/join-team" element={<JoinTeam />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ResetPassword />} />
        <Route path="/logout" element={<Logout />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <VerticalLayout />
            </PrivateRoute>
          }
        >
          <Route index path="" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />
          <Route path="team-management" element={<TeamManagement />} />
          <Route path="crm" element={<Crm />} />
          <Route path="crm/events-list/:type" element={<EventList />} />
          <Route path="crm/event-manage" element={<EventManage />} />
          <Route path="crm/event-manage/:type" element={<EventManage />} />

          <Route path="branding" element={<Branding />} />
          <Route path="automated-gifts" element={<AutomatedGiftsPage />} />
          <Route path="tracking" element={<GiftTracking />} />
          <Route path="campaign" element={<Navigate to="/gift/via-campaign" replace />} />
          <Route path="campaign/new" element={<Navigate to="/gift/via-campaign/new" replace />} />
          <Route path="campaign/:id/edit" element={<LegacyCampaignEditRedirect />} />
          <Route path="billing" element={<Pricing />}>
            <Route index path="payment-methods" element={<PaymentCard />} />
            <Route path="plans" element={<Plans />} />
            <Route path="invoices" element={<Invoice />} />
          </Route>
          <Route path="reviews" element={<ReviewsPage />}>
            <Route index element={<Reviews />} />
          </Route>
          <Route path="referrals" element={<ReviewsPage />}>
            <Route index element={<Referral />} />
          </Route>
          <Route path="gift" element={<Gift />}>
            <Route path="email-gift-link" element={<SendViaEmail />} />
            <Route path="generate-unique-link" element={<Navigate to="/gift/via-link" replace />} />
            <Route path="universal-link" element={<Navigate to="/gift/via-universal-link" replace />} />
            <Route path="schedule-send" element={<Navigate to="/gift/via-campaign" replace />} />
            <Route path="via-link" element={<SendViaLink />} />
            <Route path="via-text" element={<SendViaText />} />
            <Route path="via-universal-link" element={<BulkSend />} />
            <Route path="via-campaign/new" element={<NewCampaign />} />
            <Route path="via-campaign/:id/edit" element={<EditCampaign />} />
            <Route path="via-campaign/:id/recipients" element={<EditCampaign recipientsOnly />} />
            <Route path="via-campaign" element={<Campaign />} />
          </Route>
          <Route path="gift-settings" element={<Settings />}>
            <Route index element={<SettingsPage />} />
            <Route path="other" element={<SettingsPage />} />
          </Route>
          <Route path="templates" element={<Settings />}>
            <Route index path="list" element={<TemplateList />} />
            <Route path="list/:occasion" element={<TemplateList />} />
            <Route path="list/:occasion/:templateId" element={<TemplateView />} />
            <Route path="add" element={<AddEditTemplatePage />} />
            <Route path="edit/:id" element={<AddEditTemplatePage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;


